import type {
  AdBatchDecision,
  AdDecision,
  BannerAsset,
  ImageAsset,
  InterstitialAsset,
  NativeAsset,
  RuntimeConfig,
  V1AdBatchDecisionResponse,
  V1AdDecisionResponse,
  V1AdRequestSignals,
  VideoAsset,
} from "./types";
import { collectUserAgent } from "./signals";
import {
  buildAdRequestUrl,
  buildBatchAdRequestUrl,
  resolveAgainstApiOrigin,
} from "./url";

const MAX_CREATIVE_DIMENSION_PX = 8192;
const MAX_CREATIVE_AREA_PX = 16_777_216;

export class AdSdkError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "AdSdkError";
  }
}

export class AdConfigError extends AdSdkError {
  constructor(message: string) {
    super(message, "CONFIG_ERROR");
    this.name = "AdConfigError";
  }
}

export class AdInputError extends AdSdkError {
  constructor(message: string) {
    super(message, "INPUT_ERROR");
    this.name = "AdInputError";
  }
}

export class AdRequestError extends AdSdkError {
  constructor(message: string) {
    super(message, "REQUEST_ERROR");
    this.name = "AdRequestError";
  }
}

export class AdResponseError extends AdSdkError {
  constructor(message: string) {
    super(message, "RESPONSE_ERROR");
    this.name = "AdResponseError";
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function hasSafeCreativeDimensions(width: number, height: number): boolean {
  return (
    width <= MAX_CREATIVE_DIMENSION_PX &&
    height <= MAX_CREATIVE_DIMENSION_PX &&
    width * height <= MAX_CREATIVE_AREA_PX
  );
}

function assertSafeCreativeDimensions(
  width: number,
  height: number,
  name: string,
): void {
  if (!hasSafeCreativeDimensions(width, height)) {
    throw new AdResponseError(
      `Malformed ad response: ${name} dimensions exceed safe rendering limits.`,
    );
  }
}

function normalizeAssetUrl(config: RuntimeConfig, value: unknown): string {
  if (!isNonEmptyString(value)) {
    throw new AdResponseError("Malformed ad response: missing creative asset URL.");
  }

  const resolved = resolveAgainstApiOrigin(config.baseUrl, value);

  if (!resolved) {
    throw new AdResponseError("Malformed ad response: missing creative asset URL.");
  }

  return resolved;
}

function normalizeOptionalUrl(
  config: RuntimeConfig,
  value: unknown,
): string | undefined {
  return isNonEmptyString(value)
    ? resolveAgainstApiOrigin(config.baseUrl, value)
    : undefined;
}

function normalizeBannerAsset(
  config: RuntimeConfig,
  asset: unknown,
): BannerAsset {
  if (!asset || typeof asset !== "object") {
    throw new AdResponseError("Malformed ad response: invalid banner asset.");
  }

  const bannerAsset = asset as Partial<BannerAsset>;

  if (
    !isPositiveFiniteNumber(bannerAsset.width) ||
    !isPositiveFiniteNumber(bannerAsset.height)
  ) {
    throw new AdResponseError("Malformed ad response: invalid banner dimensions.");
  }

  assertSafeCreativeDimensions(
    bannerAsset.width,
    bannerAsset.height,
    "banner",
  );

  return Object.freeze({
    url: normalizeAssetUrl(config, bannerAsset.url),
    width: bannerAsset.width,
    height: bannerAsset.height,
  });
}

function normalizeOptionalImageAsset(
  config: RuntimeConfig,
  value: unknown,
  name: string,
): ImageAsset | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (!value || typeof value !== "object") {
    throw new AdResponseError(`Malformed ad response: invalid native ${name}.`);
  }

  const asset = value as Partial<ImageAsset>;

  if (
    !isPositiveFiniteNumber(asset.width) ||
    !isPositiveFiniteNumber(asset.height)
  ) {
    throw new AdResponseError(
      `Malformed ad response: invalid native ${name} dimensions.`,
    );
  }

  assertSafeCreativeDimensions(asset.width, asset.height, `native ${name}`);

  return Object.freeze({
    url: normalizeAssetUrl(config, asset.url),
    width: asset.width,
    height: asset.height,
  });
}

function normalizeVideoAsset(
  config: RuntimeConfig,
  asset: unknown,
): VideoAsset {
  if (!asset || typeof asset !== "object") {
    throw new AdResponseError("Malformed ad response: invalid video asset.");
  }

  const videoAsset = asset as Partial<VideoAsset>;
  const width = isPositiveFiniteNumber(videoAsset.width)
    ? videoAsset.width
    : undefined;
  const height = isPositiveFiniteNumber(videoAsset.height)
    ? videoAsset.height
    : undefined;

  if (width !== undefined && width > MAX_CREATIVE_DIMENSION_PX) {
    throw new AdResponseError(
      "Malformed ad response: video width exceeds safe rendering limits.",
    );
  }

  if (height !== undefined && height > MAX_CREATIVE_DIMENSION_PX) {
    throw new AdResponseError(
      "Malformed ad response: video height exceeds safe rendering limits.",
    );
  }

  if (width !== undefined && height !== undefined) {
    assertSafeCreativeDimensions(width, height, "video");
  }

  return {
    url: normalizeAssetUrl(config, videoAsset.url),
    posterUrl: normalizeOptionalUrl(config, videoAsset.posterUrl),
    duration: isPositiveFiniteNumber(videoAsset.duration)
      ? videoAsset.duration
      : undefined,
    width,
    height,
  };
}

function normalizeNativeAsset(
  config: RuntimeConfig,
  asset: unknown,
): NativeAsset {
  if (!asset || typeof asset !== "object") {
    throw new AdResponseError("Malformed ad response: invalid native asset.");
  }

  const nativeAsset = asset as Partial<NativeAsset>;

  if (
    !isNonEmptyString(nativeAsset.title) ||
    !isNonEmptyString(nativeAsset.description)
  ) {
    throw new AdResponseError("Malformed ad response: invalid native asset.");
  }

  return {
    title: nativeAsset.title,
    description: nativeAsset.description,
    image: normalizeOptionalImageAsset(config, nativeAsset.image, "image"),
    icon: normalizeOptionalImageAsset(config, nativeAsset.icon, "icon"),
    cta: isNonEmptyString(nativeAsset.cta) ? nativeAsset.cta : undefined,
  };
}

export function normalizeAdDecision(
  config: RuntimeConfig,
  value: unknown,
): AdDecision | null {
  if (value === null) {
    return null;
  }

  if (!value || typeof value !== "object") {
    throw new AdResponseError("Malformed ad response.");
  }

  const ad = value as V1AdDecisionResponse;

  if (!isNonEmptyString(ad.id) || !isNonEmptyString(ad.format)) {
    throw new AdResponseError("Malformed ad response.");
  }

  if (
    ad.format !== "banner" &&
    ad.format !== "video" &&
    ad.format !== "interstitial" &&
    ad.format !== "native"
  ) {
    throw new AdResponseError("Malformed ad response: unsupported ad format.");
  }

  const asset =
    ad.format === "banner"
      ? normalizeBannerAsset(config, ad.asset)
      : ad.format === "interstitial"
        ? (normalizeBannerAsset(config, ad.asset) as InterstitialAsset)
        : ad.format === "video"
          ? normalizeVideoAsset(config, ad.asset)
          : normalizeNativeAsset(config, ad.asset);

  return {
    id: ad.id,
    format: ad.format,
    clickUrl: normalizeOptionalUrl(config, ad.clickUrl),
    impressionUrl: normalizeOptionalUrl(config, ad.impressionUrl),
    clickTrackingUrl: normalizeOptionalUrl(config, ad.clickTrackingUrl),
    asset,
  };
}

export async function requestAd(
  config: RuntimeConfig,
  placementId: string,
  signals: V1AdRequestSignals,
): Promise<V1AdDecisionResponse | null> {
  const controller =
    typeof AbortController === "undefined" ? undefined : new AbortController();
  const timeoutId = controller
    ? globalThis.setTimeout(() => controller.abort(), config.timeoutMs)
    : undefined;

  try {
    const response = await fetch(buildAdRequestUrl(config.baseUrl, placementId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(signals),
      signal: controller?.signal,
    });

    if (!response.ok) {
      throw new AdRequestError(`Ad request failed with status ${response.status}.`);
    }

    let responseBody: unknown;

    try {
      responseBody = await response.json();
    } catch {
      throw new AdResponseError("Malformed ad response: invalid JSON.");
    }

    return normalizeAdDecision(config, responseBody);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new AdRequestError("Ad request timed out.");
    }

    if (error instanceof AdSdkError) {
      throw error;
    }

    throw new AdRequestError("Ad request failed.");
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

export function normalizeBatchAdDecisions(
  config: RuntimeConfig,
  placementIds: string[],
  value: unknown,
): AdBatchDecision[] {
  if (!Array.isArray(value)) {
    throw new AdResponseError("Malformed batch ad response: expected an array.");
  }

  if (value.length !== placementIds.length) {
    throw new AdResponseError(
      "Malformed batch ad response: result count does not match placement count.",
    );
  }

  return value.map((item, index) => {
    if (!item || typeof item !== "object") {
      throw new AdResponseError("Malformed batch ad response: invalid result.");
    }

    const result = item as Partial<V1AdBatchDecisionResponse>;
    if (
      !isNonEmptyString(result.placementId) ||
      result.placementId !== placementIds[index] ||
      !("ad" in result)
    ) {
      throw new AdResponseError(
        "Malformed batch ad response: placements are missing or out of order.",
      );
    }

    return {
      placementId: result.placementId,
      ad: normalizeAdDecision(config, result.ad),
    };
  });
}

export async function requestAds(
  config: RuntimeConfig,
  placementIds: string[],
  signals: V1AdRequestSignals,
): Promise<AdBatchDecision[]> {
  if (!Array.isArray(placementIds) || placementIds.length === 0) {
    throw new AdInputError("placementIds must contain at least one placement.");
  }

  const normalizedPlacementIds = placementIds.map((placementId) => {
    if (typeof placementId !== "string" || !placementId.trim()) {
      throw new AdInputError("Each placementId is required.");
    }

    return placementId.trim();
  });
  const { visitorId, ...batchSignals } = signals;
  const controller =
    typeof AbortController === "undefined" ? undefined : new AbortController();
  const timeoutId = controller
    ? globalThis.setTimeout(() => controller.abort(), config.timeoutMs)
    : undefined;

  try {
    const response = await fetch(buildBatchAdRequestUrl(config.baseUrl), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...(visitorId !== undefined ? { visitorId } : {}),
        placementIds: normalizedPlacementIds,
        userAgent: collectUserAgent(),
        signals: batchSignals,
      }),
      signal: controller?.signal,
    });

    if (!response.ok) {
      throw new AdRequestError(
        `Batch ad request failed with status ${response.status}.`,
      );
    }

    let responseBody: unknown;

    try {
      responseBody = await response.json();
    } catch {
      throw new AdResponseError("Malformed batch ad response: invalid JSON.");
    }

    return normalizeBatchAdDecisions(
      config,
      normalizedPlacementIds,
      responseBody,
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new AdRequestError("Batch ad request timed out.");
    }

    if (error instanceof AdSdkError) {
      throw error;
    }

    throw new AdRequestError("Batch ad request failed.");
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}
